'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  color: string;
}

interface ProjectStatsProps {
  stats: Stat[];
}

const ProjectStats = ({ stats }: ProjectStatsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [animatedValues, setAnimatedValues] = useState<number[]>([]);

  useEffect(() => {
    setIsHydrated(true);
    setAnimatedValues(stats.map(() => 0));
  }, [stats]);

  useEffect(() => {
    if (isHydrated) {
      stats.forEach((stat, index) => {
        const duration = 2000;
        const steps = 60;
        const increment = stat.value / steps;
        let currentStep = 0;

        const timer = setInterval(() => {
          currentStep++;
          if (currentStep <= steps) {
            setAnimatedValues((prev) => {
              const newValues = [...prev];
              newValues[index] = Math.min(Math.round(increment * currentStep), stat.value);
              return newValues;
            });
          } else {
            clearInterval(timer);
          }
        }, duration / steps);

        return () => clearInterval(timer);
      });
    }
  }, [isHydrated, stats]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:55:6" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="55" data-component-end-line="63" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((_, index) => (
          <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:57:10" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="57" data-component-end-line="61" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-6%20shadow-subtle%22%7D" key={index} className="bg-card rounded-xl p-6 shadow-subtle">
            <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:58:12" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="58" data-component-end-line="58" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20rounded-full%20bg-muted%20mb-4%22%7D" className="w-12 h-12 rounded-full bg-muted mb-4" />
            <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:59:12" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="59" data-component-end-line="59" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-24%20mb-2%22%7D" className="h-8 bg-muted rounded w-24 mb-2" />
            <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:60:12" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="60" data-component-end-line="60" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20w-32%22%7D" className="h-4 bg-muted rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:68:4" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="68" data-component-end-line="86" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:70:8" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="70" data-component-end-line="84" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-6%20shadow-subtle%20hover%3Ashadow-gold%20transition-all%20duration-300%20hover%3A-translate-y-1%20border%20border-border%22%7D"
          key={index}
          className="bg-card rounded-xl p-6 shadow-subtle hover:shadow-gold transition-all duration-300 hover:-translate-y-1 border border-border"
        >
          <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:74:10" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="74" data-component-end-line="78" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${stat.color}`}
          >
            <Icon data-component-id="src/app/process-timeline/components/ProjectStats.tsx:77:12" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="77" data-component-end-line="77" data-component-file="ProjectStats.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-white%22%7D" name={stat.icon as any} size={24} variant="solid" className="text-white" />
          </div>
          <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:79:10" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="79" data-component-end-line="82" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-4xl%20text-text-primary%20mb-1%22%7D" className="font-playfair font-bold text-4xl text-text-primary mb-1">
            {animatedValues[index]}
            {stat.suffix}
          </div>
          <div data-component-id="src/app/process-timeline/components/ProjectStats.tsx:83:10" data-component-path="src/app/process-timeline/components/ProjectStats.tsx" data-component-line="83" data-component-end-line="83" data-component-file="ProjectStats.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-sans%20text-sm%20text-text-secondary%22%7D" className="font-sans text-sm text-text-secondary">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

export default ProjectStats;